import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Pencil,
  Type,
  Languages,
  Sparkles,
  FolderClosed,
  Plus,
  X,
  RotateCcw,
  Shuffle,
  Wand2,
  NotebookPen,
  Layers,
  Trash2,
  BookOpen,
  Users,
} from "lucide-react";
import {
  STORAGE,
  LANGUAGES,
  DEFAULT_DECOYS,
  loadDecoys,
  saveDecoys,
  newDecoy,
  shuffleDecoys,
  MAX_DECOYS,
  getRevealStyle,
  getDefaultTitle,
  getSwiperConfig,
  saveSwiperCombos,
  makeSwiperComboId,
  getTenOutsConfig,
  saveTenOutsWords,
  saveTenOutsInterval,
  getTridentConfig,
  saveTridentEnabled,
  saveTridentTemplate,
  DEFAULT_TRIDENT_TEMPLATE,
  DEFAULT_SEARCH_FORCE_TEMPLATE,
  DEFAULT_SEARCH_FORCE_TITLE,
  DEFAULT_SEARCH_FORCE_DATE_LABEL,
  DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES,
  DEFAULT_SEARCH_FORCE_Q1_CANCEL,
  DEFAULT_SEARCH_FORCE_Q1_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q2_CANCEL,
  DEFAULT_SEARCH_FORCE_Q2_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q3_CANCEL,
  DEFAULT_SEARCH_FORCE_Q3_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q4_CANCEL,
  DEFAULT_SEARCH_FORCE_Q4_EMERGENCY,
  getSearchForceConfig,
  saveSearchForceEnabled,
  setSearchForceFrozenDate,
  clearSearchForceFrozenDate,
  clearSearchForcePersisted,
  saveSearchForceTemplate,
  saveSearchForceTitle,
  saveSearchForceDateLabel,
  saveSearchForceDateISO,
  saveSearchForceDateOffset,
  saveSearchForceQ1CancelLabel,
  saveSearchForceQ1EmergencyLabel,
  saveSearchForceQ2CancelLabel,
  saveSearchForceQ2EmergencyLabel,
  saveSearchForceQ3CancelLabel,
  saveSearchForceQ3EmergencyLabel,
  saveSearchForceQ4CancelLabel,
  saveSearchForceQ4EmergencyLabel,
  getSearchForceArmed,
  clearSearchForceArmed,
  apocalypseCardLabel,
  MIN_INTERVAL_SEC,
  MAX_INTERVAL_SEC,
  getAddNumberConfig,
  DEFAULT_FORCE_TOTAL,
  getApocalypseConfig,
  saveApocalypseLength,
  saveApocalypseDelay,
  saveApocalypseWipeFrameMs,
  saveApocalypsePostWipeDelayMs,
  saveApocalypseShakeThreshold,
  saveApocalypseShakeCount,
  saveApocalypseAutoMotion,
  saveApocalypseFxPermEnabled,
  saveApocalypseFxPermType,
  saveApocalypseFxSpeechEnabled,
  saveApocalypseFxSpeechTemplate,
  saveApocalypseFxSpeechVoice,
  saveApocalypseFxSpeechRate,
  saveApocalypseFxSpeechPitch,
  saveApocalypseFxClipboardEnabled,
  saveApocalypseFxClipboardTemplate,
  saveApocalypseFxThemeEnabled,
  saveApocalypseFxThemeColors,
  saveApocalypseFxThemeInterval,
  saveApocalypseResetPreset,
  saveApocalypseFlashDelayMs,
  saveApocalypseFlashCount,
  saveApocalypseFlashDurationMs,
  saveApocalypseFlashGapMs,
  // (saveApocalypseCueDotPosition removed — cue dot now sits inline next to the counter)
  saveApocalypseCueDotSizePx,
  saveApocalypseCueDotColor,
  saveApocalypseCueDotOpacityPct,
  saveApocalypseCueDotLeadSec,
  // Pass-4
  saveApocalypseWipeMode,
  saveApocalypseErrorEnabled,
  saveApocalypseErrorTitle,
  saveApocalypseErrorBody,
  saveApocalypseErrorSoundVolume,
  saveApocalypseVibrationEnabled,
  saveApocalypseVibrationVolume,
  saveApocalypseSecondTemplate,
  saveApocalypseShakeMode,
  saveApocalypsePushEnabled,
  saveApocalypsePushSoundEnabled,
  saveApocalypseScrollSpeedMs,
  saveApocalypseRestoreFlashEnabled,
  saveApocalypseRestoreFlashMs,
  APOCALYPSE_WIPE_MODES,
  APOCALYPSE_SHAKE_MODES,
  DEFAULT_APOCALYPSE_ERROR_TITLE,
  DEFAULT_APOCALYPSE_ERROR_BODY,
  DEFAULT_APOCALYPSE_SECOND_TEMPLATE,
  APOCALYPSE_FX_PERM_TYPES,
  DEFAULT_APOCALYPSE_FX_PERM_TYPE,
  DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE,
  DEFAULT_APOCALYPSE_FX_SPEECH_RATE,
  MIN_APOCALYPSE_FX_SPEECH_RATE,
  MAX_APOCALYPSE_FX_SPEECH_RATE,
  DEFAULT_APOCALYPSE_FX_SPEECH_PITCH,
  MIN_APOCALYPSE_FX_SPEECH_PITCH,
  MAX_APOCALYPSE_FX_SPEECH_PITCH,
  DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE,
  DEFAULT_APOCALYPSE_FX_THEME_COLORS,
  DEFAULT_APOCALYPSE_FX_THEME_INTERVAL,
  MIN_APOCALYPSE_FX_THEME_INTERVAL,
  MAX_APOCALYPSE_FX_THEME_INTERVAL,
  MIN_APOCALYPSE_FLASH_DELAY_MS,
  MAX_APOCALYPSE_FLASH_DELAY_MS,
  MIN_APOCALYPSE_FLASH_COUNT,
  MAX_APOCALYPSE_FLASH_COUNT,
  MIN_APOCALYPSE_FLASH_DURATION_MS,
  MAX_APOCALYPSE_FLASH_DURATION_MS,
  MIN_APOCALYPSE_FLASH_GAP_MS,
  MAX_APOCALYPSE_FLASH_GAP_MS,
  // (cue-dot position picker removed — cue dot now sits inline next to the notes counter)
  MIN_APOCALYPSE_CUE_DOT_SIZE_PX,
  MAX_APOCALYPSE_CUE_DOT_SIZE_PX,
  MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT,
  MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT,
  MIN_APOCALYPSE_CUE_DOT_LEAD_SEC,
  MAX_APOCALYPSE_CUE_DOT_LEAD_SEC,
  MIN_APOCALYPSE_DELAY,
  MAX_APOCALYPSE_DELAY,
  DEFAULT_APOCALYPSE_TEMPLATE,
  MIN_APOCALYPSE_WIPE_FRAME_MS,
  MAX_APOCALYPSE_WIPE_FRAME_MS,
  DEFAULT_APOCALYPSE_WIPE_FRAME_MS,
  MIN_APOCALYPSE_POST_WIPE_DELAY_MS,
  MAX_APOCALYPSE_POST_WIPE_DELAY_MS,
  DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS,
  MIN_APOCALYPSE_SHAKE_THRESHOLD,
  MAX_APOCALYPSE_SHAKE_THRESHOLD,
  DEFAULT_APOCALYPSE_SHAKE_THRESHOLD,
  MIN_APOCALYPSE_SHAKE_COUNT,
  MAX_APOCALYPSE_SHAKE_COUNT,
  DEFAULT_APOCALYPSE_SHAKE_COUNT,
  saveApocalypseExtrasCount,
  saveApocalypseExtrasInterval,
  saveApocalypseExtrasTitles,
  saveApocalypseFlickerMs,
  saveApocalypseFlashColor,
  saveApocalypseRowStagger,
  saveApocalypseRowCycle,
  saveApocalypseRowFrame,
  MIN_APOCALYPSE_EXTRAS_COUNT,
  MAX_APOCALYPSE_EXTRAS_COUNT,
  MIN_APOCALYPSE_EXTRAS_INTERVAL,
  MAX_APOCALYPSE_EXTRAS_INTERVAL,
  MIN_APOCALYPSE_FLICKER_MS,
  MAX_APOCALYPSE_FLICKER_MS,
  MIN_APOCALYPSE_ROW_STAGGER,
  MAX_APOCALYPSE_ROW_STAGGER,
  MIN_APOCALYPSE_ROW_CYCLE,
  MAX_APOCALYPSE_ROW_CYCLE,
  MIN_APOCALYPSE_ROW_FRAME,
  MAX_APOCALYPSE_ROW_FRAME,
  DEFAULT_APOCALYPSE_EXTRAS_COUNT,
  DEFAULT_APOCALYPSE_EXTRAS_INTERVAL,
  DEFAULT_APOCALYPSE_EXTRAS_TITLES,
  DEFAULT_APOCALYPSE_FLICKER_MS,
  DEFAULT_APOCALYPSE_FLASH_COLORS,
  DEFAULT_APOCALYPSE_ROW_STAGGER,
  DEFAULT_APOCALYPSE_ROW_CYCLE,
  DEFAULT_APOCALYPSE_ROW_FRAME,
  getFlipForceConfig,
  saveFlipForceEnabled,
  saveFlipForceTargetId,
  saveFlipForceTargetId2,
  saveFlipForceTargetId3,
  saveFlipForceMinFacedownMs,
} from "@/lib/mr";
import { Input } from "@/components/ui/input";
import { BrandWordmark } from "@/components/Brand";
import ApocalypseSettings from "./ApocalypseSettings";
// PredictionLinkCard is kept on disk but no longer mounted — feature
// disabled per user request 2026-02-18. Backend endpoints intentionally
// left intact so this UI can be brought back at any time.
import {
  BackdateCard,
  CompactRevealCard,
  TenOutsCard,
  TridentCard,
  AddANumberCard,
  PhantomPeekCard,
  FlipForceCard,
  SearchForceCard,
  HapticsCard,
  DecoyResetCard,
  ICloudBannerCard,
} from "./SettingsCards";

// Reveal styles for the long-press / ellipsis answer surfacing.
const REVEAL_STYLES = [
  { id: "peek",    label: "Secret peek", desc: "Subliminal answer appears in the date line." },
  { id: "scroll",  label: "Scroll peek", desc: "Hides the answer below the fold." },
  { id: "whisper", label: "Whisper",     desc: "Speaks the answer through your earbud." },
];

// Custom-mode combos — each row is a 1-3 swipe sequence + the word that
// fills (xxx) when the magician performs that sequence on the folder list.
const DIR_OPTIONS = [
  { value: "_",  label: "—" },
  { value: "U",  label: "↑" },
  { value: "R",  label: "→" },
  { value: "D",  label: "↓" },
  { value: "L",  label: "←" },
];
const ARROW = { U: "↑", R: "→", D: "↓", L: "←" };

export default function Settings() {
  const navigate = useNavigate();

  const [language, setLanguage] = useState(
    () => localStorage.getItem(STORAGE.LANGUAGE) || "auto"
  );
  const [decoys, setDecoys] = useState(() => loadDecoys());
  const [revealStyle, setRevealStyle] = useState(() => getRevealStyle());
  const [predictionPrefix, setPredictionPrefix] = useState(
    () => localStorage.getItem(STORAGE.PREDICTION_PREFIX) || ""
  );
  const [backdateMin, setBackdateMin] = useState(() => {
    const v = parseInt(localStorage.getItem(STORAGE.BACKDATE_MIN) || "0", 10);
    return Number.isFinite(v) && v >= 0 ? Math.min(v, 180) : 0;
  });
  const [defaultTitle, setDefaultTitle] = useState(() => getDefaultTitle());
  const [composeTitle, setComposeTitle] = useState(
    () => localStorage.getItem(STORAGE.COMPOSE_TITLE) ?? ""
  );
  const [compactReveal, setCompactReveal] = useState(
    () => localStorage.getItem(STORAGE.COMPACT_REVEAL) === "1"
  );

  // --- Realism: haptic feedback -----------------------------------------
  // Default ON. Persisted immediately on toggle so the LockScreen /
  // DecoyNotes / Trick handlers see the change without a reload.
  const [hapticsEnabled, setHapticsEnabledState] = useState(() => {
    const v = localStorage.getItem(STORAGE.HAPTICS_ENABLED);
    return v === null ? true : v === "1";
  });
  const setHapticsEnabled = (v) => {
    setHapticsEnabledState(v);
    localStorage.setItem(STORAGE.HAPTICS_ENABLED, v ? "1" : "0");
  };

  // --- Realism: decoy notes reset on exit -------------------------------
  // Default ON. Only affects `plain` decoys — compose (+) notes keep
  // their normal persistent behaviour.
  const [decoyResetOnExit, setDecoyResetOnExitState] = useState(() => {
    const v = localStorage.getItem(STORAGE.DECOY_RESET_ON_EXIT);
    return v === null ? true : v === "1";
  });
  const setDecoyResetOnExit = (v) => {
    setDecoyResetOnExitState(v);
    localStorage.setItem(STORAGE.DECOY_RESET_ON_EXIT, v ? "1" : "0");
  };

  // --- Realism: iCloud "Storage Almost Full" banner ---------------------
  // Default ON. Purely cosmetic — hardens the disguise on /notes.
  const [iCloudBannerEnabled, setICloudBannerEnabledState] = useState(() => {
    const v = localStorage.getItem(STORAGE.ICLOUD_BANNER_ENABLED);
    return v === null ? true : v === "1";
  });
  const setICloudBannerEnabled = (v) => {
    setICloudBannerEnabledState(v);
    localStorage.setItem(STORAGE.ICLOUD_BANNER_ENABLED, v ? "1" : "0");
  };

  // --- Phantom Peek state -------------------------------------------------
  // Default ON — the feature is opt-in via the user-flow (compose + swipe
  // delete) so leaving the toggle live by default is safe.
  const [peekEnabled, setPeekEnabled] = useState(() => {
    const v = localStorage.getItem(STORAGE.PEEK_ENABLED);
    return v === null ? true : v === "1";
  });
  const [peekDurationMs, setPeekDurationMs] = useState(() => {
    const v = parseInt(localStorage.getItem(STORAGE.PEEK_DURATION_MS) || "2000", 10);
    return Number.isFinite(v) ? Math.max(500, Math.min(5000, v)) : 2000;
  });
  const [peekPassMotion, setPeekPassMotion] = useState(
    () => localStorage.getItem(STORAGE.PEEK_PASS_MOTION) === "1"
  );
  const [peekPassThreshold, setPeekPassThreshold] = useState(() => {
    const v = parseInt(localStorage.getItem(STORAGE.PEEK_PASS_THRESHOLD) || "9", 10);
    return Number.isFinite(v) ? Math.max(6, Math.min(18, v)) : 9;
  });

  // --- Flip Force state (face-down dwell → force-open target decoy) -------
  // Pure config: the actual devicemotion listener lives in DecoyNotes.jsx.
  // Permission for DeviceMotionEvent is requested inside the user-gesture
  // toggle handler (see FlipForceCard) — required on iOS 13+.
  const [initialFlipForce] = useState(() => getFlipForceConfig());
  const [flipForceEnabled, setFlipForceEnabled] = useState(initialFlipForce.enabled);
  const [flipForceTargetId, setFlipForceTargetId] = useState(initialFlipForce.targetId);
  const [flipForceTargetId2, setFlipForceTargetId2] = useState(initialFlipForce.targetId2);
  const [flipForceTargetId3, setFlipForceTargetId3] = useState(initialFlipForce.targetId3);
  const [flipForceMinFacedownMs, setFlipForceMinFacedownMs] = useState(initialFlipForce.minFacedownMs);

  // --- Swiper Mode state (top decoy prediction) ---------------------------
  const initialSwiper = useState(() => getSwiperConfig())[0];
  const [swiperEnabled, setSwiperEnabled] = useState(initialSwiper.enabled);
  const [swiperMode, setSwiperMode] = useState(initialSwiper.mode);
  const [swiperFormat, setSwiperFormat] = useState(initialSwiper.format);
  const [swiperTemplate, setSwiperTemplate] = useState(initialSwiper.template);
  const [swiperCombos, setSwiperCombos] = useState(() =>
    initialSwiper.combos.length
      ? initialSwiper.combos
      : [{ id: makeSwiperComboId(), dirs: "U", word: "" }]
  );

  // --- Ten Outs state (third decoy) --------------------------------------
  const [initialTenOuts] = useState(() => getTenOutsConfig());
  const [tenOutsEnabled, setTenOutsEnabled] = useState(initialTenOuts.enabled);
  const [tenOutsInterval, setTenOutsIntervalState] = useState(initialTenOuts.interval);
  const [tenOutsTemplate, setTenOutsTemplate] = useState(initialTenOuts.template);
  const [tenOutsWords, setTenOutsWordsState] = useState(initialTenOuts.words);

  // --- Trident state (second decoy — chronological-order reveal) ----------
  const [initialTrident] = useState(() => getTridentConfig());
  const [tridentEnabled, setTridentEnabled] = useState(initialTrident.enabled);
  const [tridentTemplate, setTridentTemplate] = useState(initialTrident.template);

  // --- Search-Force state (fake old prediction note injected atop search results) --
  const [initialSearchForce] = useState(() => getSearchForceConfig());
  const [searchForceEnabled, setSearchForceEnabled] = useState(initialSearchForce.enabled);
  const [searchForceTitle, setSearchForceTitle] = useState(initialSearchForce.title);
  const [searchForceTemplate, setSearchForceTemplate] = useState(initialSearchForce.template);
  const [searchForceDateLabel, setSearchForceDateLabel] = useState(initialSearchForce.dateLabel);
  // New structured "Note date (top of note)" — an ISO datetime that
  // drives BOTH the displayed header AND the folder bucketing so the
  // persisted prediction lands in the correct day-group. When empty
  // the code falls back to the legacy free-form dateLabel above.
  const [searchForceDateISO, setSearchForceDateISO] = useState(
    initialSearchForce.dateISO || "",
  );
  const [searchForceDateOffset, setSearchForceDateOffset] = useState(
    String(initialSearchForce.dateOffsetMinutes),
  );
  // Six editable labels for the 3-question personalisation flow. Each
  // question has a Cancel-side and Emergency-side word; getSearchForceConfig
  // already falls back to the DEFAULT_SEARCH_FORCE_Q*_* constants when
  // no localStorage override exists, so `initialSearchForce.qNCancelLabel`
  // is always a non-empty string.
  const [searchForceQ1Cancel, setSearchForceQ1Cancel] = useState(initialSearchForce.q1CancelLabel);
  const [searchForceQ1Emergency, setSearchForceQ1Emergency] = useState(initialSearchForce.q1EmergencyLabel);
  const [searchForceQ2Cancel, setSearchForceQ2Cancel] = useState(initialSearchForce.q2CancelLabel);
  const [searchForceQ2Emergency, setSearchForceQ2Emergency] = useState(initialSearchForce.q2EmergencyLabel);
  const [searchForceQ3Cancel, setSearchForceQ3Cancel] = useState(initialSearchForce.q3CancelLabel);
  const [searchForceQ3Emergency, setSearchForceQ3Emergency] = useState(initialSearchForce.q3EmergencyLabel);
  const [searchForceQ4Cancel, setSearchForceQ4Cancel] = useState(initialSearchForce.q4CancelLabel);
  const [searchForceQ4Emergency, setSearchForceQ4Emergency] = useState(initialSearchForce.q4EmergencyLabel);
  // Lockscreen-armed indicator — reflected in the SearchForceCard so the
  // magician always knows if a live card is queued up. Re-reads on every
  // Settings mount so opening the panel after arming shows the fresh card.
  const [searchForceArmedInfo, setSearchForceArmedInfo] = useState(() => {
    const armed = getSearchForceArmed();
    if (!armed) return null;
    const langCode =
      localStorage.getItem("mr_lang") || "auto";
    let cardLabel = "";
    if (typeof armed.card === "string") {
      cardLabel = armed.card;
    } else {
      try { cardLabel = apocalypseCardLabel(armed.card, langCode) || ""; }
      catch { cardLabel = ""; }
    }
    return { cardLabel, armedAt: armed.armedAt };
  });
  const handleClearSearchForceArmed = () => {
    clearSearchForceArmed();
    setSearchForceArmedInfo(null);
  };

  // --- Add-a-Number state (per-note armed via pencil; config lives here) -
  const [initialAddNum] = useState(() => getAddNumberConfig());
  const [addNumForceTotal, setAddNumForceTotal] = useState(
    String(initialAddNum.forceTotal),
  );
  const [addNumSwapPosition, setAddNumSwapPosition] = useState(
    String(initialAddNum.swapPosition),
  );
  const [addNumTrigger, setAddNumTrigger] = useState(initialAddNum.trigger);

  // --- Notes Apocalypse state (fake lockscreen → glitching note titles) --
  const [initialApoc] = useState(() => getApocalypseConfig());
  const [apocEnabled, setApocEnabled] = useState(initialApoc.enabled);
  const [apocLength, setApocLength] = useState(String(initialApoc.length));
  const [apocDelay, setApocDelay] = useState(String(initialApoc.delay));
  const [apocTemplate, setApocTemplate] = useState(initialApoc.template);
  const [apocWallpaper, setApocWallpaper] = useState(initialApoc.wallpaper);
  // --- Apocalypse cinematic knobs (phase 1 extras / phase 2 flicker /
  //     phase 3 resolve). Stored as strings so an empty input doesn't
  //     snap back to a default mid-edit; clamped + parsed on save. -----
  const [apocCinematicOpen, setApocCinematicOpen] = useState(false);
  const [apocWipeFrameMs, setApocWipeFrameMs] = useState(String(initialApoc.wipeFrameMs));
  const [apocPostWipeDelayMs, setApocPostWipeDelayMs] = useState(String(initialApoc.postWipeDelayMs));
  const [apocShakeThreshold, setApocShakeThreshold] = useState(String(initialApoc.shakeThreshold));
  const [apocShakeCount, setApocShakeCount] = useState(String(initialApoc.shakeCount));
  const [apocAutoMotion, setApocAutoMotion] = useState(initialApoc.autoMotion);
  const [apocMotionStatus, setApocMotionStatus] = useState("idle"); // idle|granted|denied|unsupported|prompting
  // Native FX state
  const [apocFxPermEnabled, setApocFxPermEnabled] = useState(initialApoc.fxPermEnabled);
  const [apocFxPermType, setApocFxPermType] = useState(initialApoc.fxPermType);
  const [apocFxSpeechEnabled, setApocFxSpeechEnabled] = useState(initialApoc.fxSpeechEnabled);
  const [apocFxSpeechTemplate, setApocFxSpeechTemplate] = useState(initialApoc.fxSpeechTemplate);
  const [apocFxSpeechVoice, setApocFxSpeechVoice] = useState(initialApoc.fxSpeechVoice);
  const [apocFxSpeechRate, setApocFxSpeechRate] = useState(String(initialApoc.fxSpeechRate));
  const [apocFxSpeechPitch, setApocFxSpeechPitch] = useState(String(initialApoc.fxSpeechPitch));
  const [apocFxClipboardEnabled, setApocFxClipboardEnabled] = useState(initialApoc.fxClipboardEnabled);
  const [apocFxClipboardTemplate, setApocFxClipboardTemplate] = useState(initialApoc.fxClipboardTemplate);
  const [apocFxThemeEnabled, setApocFxThemeEnabled] = useState(initialApoc.fxThemeEnabled);
  const [apocFxThemeColors, setApocFxThemeColors] = useState(initialApoc.fxThemeColors.join(", "));
  const [apocFxThemeInterval, setApocFxThemeInterval] = useState(String(initialApoc.fxThemeInterval));
  const [apocResetPreset, setApocResetPreset] = useState(initialApoc.resetPreset);
  const [apocFlashDelayMs, setApocFlashDelayMs] = useState(String(initialApoc.flashDelayMs));
  const [apocFlashCount, setApocFlashCount] = useState(String(initialApoc.flashCount));
  const [apocFlashDurationMs, setApocFlashDurationMs] = useState(String(initialApoc.flashDurationMs));
  const [apocFlashGapMs, setApocFlashGapMs] = useState(String(initialApoc.flashGapMs));
  // Cue-dot ("secret indicator") customizable knobs.
  // (apocCueDotPosition state removed — cue dot now sits inline next to the notes counter)
  const [apocCueDotSizePx, setApocCueDotSizePx] = useState(String(initialApoc.cueDot.sizePx));
  const [apocCueDotColor, setApocCueDotColor] = useState(initialApoc.cueDot.color);
  const [apocCueDotOpacityPct, setApocCueDotOpacityPct] = useState(String(initialApoc.cueDot.opacityPct));
  const [apocCueDotLeadSec, setApocCueDotLeadSec] = useState(String(initialApoc.cueDot.leadSec));
  // Rehearse-trick controls — let the magician one-tap-arm a chosen
  // dummy card and bounce straight to /lock without manually swiping
  // the deck encoding. The flag persists until the rebooted clean
  // Notes screen reloads, which then auto-navs back to /settings.
  const [rehearseValue, setRehearseValue] = useState("Q");
  const [rehearseSuit, setRehearseSuit] = useState("H");
  // Voices list — populated lazily via speechSynthesis.onvoiceschanged.
  const [voicesList, setVoicesList] = useState(
    typeof window !== "undefined" && window.speechSynthesis
      ? window.speechSynthesis.getVoices()
      : []
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const refresh = () => setVoicesList(window.speechSynthesis.getVoices());
    refresh();
    window.speechSynthesis.addEventListener?.("voiceschanged", refresh);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", refresh);
  }, []);
  // Wipe the search-force session snapshot the moment the magician
  // returns to the admin Settings screen. Per user request this is one
  // of only two hard clears (the other is a fresh camera-tap arm on
  // the fake lockscreen). Keeps the persisted body around across app-
  // refreshes, navigation to Folders, and idle time — but guarantees
  // a clean slate the moment the magician re-opens Settings to prep
  // the next trick.
  useEffect(() => {
    try { clearSearchForcePersisted(); } catch { /* ignore */ }
  }, []);
  // FX feedback toast for "Test" buttons.
  const [apocFxFeedback, setApocFxFeedback] = useState("");
  const [apocExtrasCount, setApocExtrasCount] = useState(String(initialApoc.extrasCount));
  const [apocExtrasInterval, setApocExtrasInterval] = useState(String(initialApoc.extrasInterval));
  const [apocExtrasTitles, setApocExtrasTitles] = useState(initialApoc.extrasTitles.join("\n"));
  const [apocFlickerMs, setApocFlickerMs] = useState(String(initialApoc.flickerMs));
  const [apocFlash1, setApocFlash1] = useState(initialApoc.flashColors[0]);
  const [apocFlash2, setApocFlash2] = useState(initialApoc.flashColors[1]);
  const [apocFlash3, setApocFlash3] = useState(initialApoc.flashColors[2]);
  const [apocFlash4, setApocFlash4] = useState(initialApoc.flashColors[3]);
  const [apocRowStagger, setApocRowStagger] = useState(String(initialApoc.rowStagger));
  const [apocRowCycle, setApocRowCycle] = useState(String(initialApoc.rowCycle));
  const [apocRowFrame, setApocRowFrame] = useState(String(initialApoc.rowFrame));
  // Pass-4: wipe-mode + post-reveal error dialog + second-prediction + shake mode
  const [apocWipeMode, setApocWipeMode] = useState(initialApoc.wipeMode);
  const [apocErrorEnabled, setApocErrorEnabled] = useState(initialApoc.errorEnabled);
  const [apocErrorTitle, setApocErrorTitle] = useState(initialApoc.errorTitle);
  const [apocErrorBody, setApocErrorBody] = useState(initialApoc.errorBody);
  const [apocErrorSoundVolume, setApocErrorSoundVolume] = useState(String(initialApoc.errorSoundVolume));
  const [apocVibrationEnabled, setApocVibrationEnabled] = useState(initialApoc.vibrationEnabled);
  const [apocVibrationVolume, setApocVibrationVolume] = useState(String(initialApoc.vibrationVolume));
  const [apocSecondTemplate, setApocSecondTemplate] = useState(initialApoc.secondTemplate);
  const [apocShakeMode, setApocShakeMode] = useState(initialApoc.shakeMode);
  const [apocPushEnabled, setApocPushEnabled] = useState(initialApoc.pushEnabled);
  const [apocPushSoundEnabled, setApocPushSoundEnabled] = useState(initialApoc.pushSoundEnabled);
  const [apocScrollSpeedMs, setApocScrollSpeedMs] = useState(String(initialApoc.scrollSpeedMs));
  const [apocRestoreFlashEnabled, setApocRestoreFlashEnabled] = useState(initialApoc.restoreFlashEnabled);
  const [apocRestoreFlashMs, setApocRestoreFlashMs] = useState(String(initialApoc.restoreFlashMs));

  useEffect(() => { localStorage.setItem(STORAGE.LANGUAGE, language); }, [language]);
  useEffect(() => { saveDecoys(decoys); }, [decoys]);
  useEffect(() => { localStorage.setItem(STORAGE.REVEAL_STYLE, revealStyle); }, [revealStyle]);
  useEffect(() => { localStorage.setItem(STORAGE.PREDICTION_PREFIX, predictionPrefix); }, [predictionPrefix]);
  useEffect(() => { localStorage.setItem(STORAGE.BACKDATE_MIN, String(backdateMin)); }, [backdateMin]);
  useEffect(() => { localStorage.setItem(STORAGE.DEFAULT_TITLE, defaultTitle); }, [defaultTitle]);
  useEffect(() => { localStorage.setItem(STORAGE.COMPOSE_TITLE, composeTitle); }, [composeTitle]);
  useEffect(() => { localStorage.setItem(STORAGE.COMPACT_REVEAL, compactReveal ? "1" : "0"); }, [compactReveal]);
  useEffect(() => { localStorage.setItem(STORAGE.PEEK_ENABLED, peekEnabled ? "1" : "0"); }, [peekEnabled]);
  useEffect(() => { localStorage.setItem(STORAGE.PEEK_DURATION_MS, String(peekDurationMs)); }, [peekDurationMs]);
  useEffect(() => { localStorage.setItem(STORAGE.PEEK_PASS_MOTION, peekPassMotion ? "1" : "0"); }, [peekPassMotion]);
  useEffect(() => { localStorage.setItem(STORAGE.PEEK_PASS_THRESHOLD, String(peekPassThreshold)); }, [peekPassThreshold]);
  useEffect(() => { localStorage.setItem(STORAGE.SWIPER_ENABLED, swiperEnabled ? "1" : "0"); }, [swiperEnabled]);
  useEffect(() => { localStorage.setItem(STORAGE.SWIPER_MODE, swiperMode); }, [swiperMode]);
  useEffect(() => { localStorage.setItem(STORAGE.SWIPER_FORMAT, swiperFormat); }, [swiperFormat]);
  useEffect(() => { localStorage.setItem(STORAGE.SWIPER_TEMPLATE, swiperTemplate); }, [swiperTemplate]);
  useEffect(() => { saveSwiperCombos(swiperCombos); }, [swiperCombos]);
  useEffect(() => { localStorage.setItem(STORAGE.TENOUTS_ENABLED, tenOutsEnabled ? "1" : "0"); }, [tenOutsEnabled]);
  useEffect(() => { saveTenOutsInterval(tenOutsInterval); }, [tenOutsInterval]);
  useEffect(() => { localStorage.setItem(STORAGE.TENOUTS_TEMPLATE, tenOutsTemplate); }, [tenOutsTemplate]);
  useEffect(() => { saveTenOutsWords(tenOutsWords); }, [tenOutsWords]);

  // --- Trident persistence (second decoy) ---------------------------------
  useEffect(() => { saveTridentEnabled(tridentEnabled); }, [tridentEnabled]);
  useEffect(() => { saveTridentTemplate(tridentTemplate); }, [tridentTemplate]);

  // --- Search-Force persistence -------------------------------------------
  useEffect(() => { saveSearchForceEnabled(searchForceEnabled); }, [searchForceEnabled]);
  // Freeze/unfreeze the (date) placeholder timestamp when the magician
  // flips the enabled switch. Skip the initial mount so a page reload
  // never overwrites an in-flight arm. Uses a ref sentinel because we
  // want the effect to run only on ACTUAL user toggles, not on the
  // first render where searchForceEnabled equals its saved value.
  const searchForceFreezeMountedRef = useRef(false);
  useEffect(() => {
    if (!searchForceFreezeMountedRef.current) {
      searchForceFreezeMountedRef.current = true;
      return;
    }
    if (searchForceEnabled) {
      try {
        setSearchForceFrozenDate(Number(searchForceDateOffset) || 0);
      } catch { /* ignore — live-now fallback still works */ }
    } else {
      try { clearSearchForceFrozenDate(); } catch { /* ignore */ }
      try { clearSearchForcePersisted(); } catch { /* ignore */ }
    }
    // dateOffset intentionally excluded — we snapshot its value at
    // toggle time, later edits shouldn't re-freeze silently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchForceEnabled]);
  useEffect(() => { saveSearchForceTitle(searchForceTitle); }, [searchForceTitle]);
  useEffect(() => { saveSearchForceTemplate(searchForceTemplate); }, [searchForceTemplate]);
  useEffect(() => { saveSearchForceDateLabel(searchForceDateLabel); }, [searchForceDateLabel]);
  useEffect(() => { saveSearchForceDateISO(searchForceDateISO); }, [searchForceDateISO]);
  useEffect(() => { saveSearchForceDateOffset(searchForceDateOffset); }, [searchForceDateOffset]);
  useEffect(() => { saveSearchForceQ1CancelLabel(searchForceQ1Cancel); }, [searchForceQ1Cancel]);
  useEffect(() => { saveSearchForceQ1EmergencyLabel(searchForceQ1Emergency); }, [searchForceQ1Emergency]);
  useEffect(() => { saveSearchForceQ2CancelLabel(searchForceQ2Cancel); }, [searchForceQ2Cancel]);
  useEffect(() => { saveSearchForceQ2EmergencyLabel(searchForceQ2Emergency); }, [searchForceQ2Emergency]);
  useEffect(() => { saveSearchForceQ3CancelLabel(searchForceQ3Cancel); }, [searchForceQ3Cancel]);
  useEffect(() => { saveSearchForceQ3EmergencyLabel(searchForceQ3Emergency); }, [searchForceQ3Emergency]);
  useEffect(() => { saveSearchForceQ4CancelLabel(searchForceQ4Cancel); }, [searchForceQ4Cancel]);
  useEffect(() => { saveSearchForceQ4EmergencyLabel(searchForceQ4Emergency); }, [searchForceQ4Emergency]);

  // --- Flip Force persistence ---------------------------------------------
  useEffect(() => { saveFlipForceEnabled(flipForceEnabled); }, [flipForceEnabled]);
  useEffect(() => { saveFlipForceTargetId(flipForceTargetId); }, [flipForceTargetId]);
  useEffect(() => { saveFlipForceTargetId2(flipForceTargetId2); }, [flipForceTargetId2]);
  useEffect(() => { saveFlipForceTargetId3(flipForceTargetId3); }, [flipForceTargetId3]);
  useEffect(() => { saveFlipForceMinFacedownMs(flipForceMinFacedownMs); }, [flipForceMinFacedownMs]);
  useEffect(() => {
    // Sanitise force total: only persist when it looks like a positive integer.
    const n = parseInt(addNumForceTotal, 10);
    if (Number.isFinite(n) && n > 0) {
      localStorage.setItem(STORAGE.ADDNUM_FORCE_TOTAL, String(n));
    }
  }, [addNumForceTotal]);
  useEffect(() => { localStorage.setItem(STORAGE.ADDNUM_SWAP_POSITION, addNumSwapPosition); }, [addNumSwapPosition]);
  useEffect(() => { localStorage.setItem(STORAGE.ADDNUM_TRIGGER, addNumTrigger); }, [addNumTrigger]);
  useEffect(() => { localStorage.setItem(STORAGE.APOCALYPSE_ENABLED, apocEnabled ? "1" : "0"); }, [apocEnabled]);
  useEffect(() => { saveApocalypseLength(apocLength); }, [apocLength]);
  useEffect(() => { saveApocalypseDelay(apocDelay); }, [apocDelay]);
  useEffect(() => { saveApocalypseResetPreset(apocResetPreset); }, [apocResetPreset]);
  useEffect(() => { saveApocalypseFlashDelayMs(apocFlashDelayMs); }, [apocFlashDelayMs]);
  useEffect(() => { saveApocalypseFlashCount(apocFlashCount); }, [apocFlashCount]);
  useEffect(() => { saveApocalypseFlashDurationMs(apocFlashDurationMs); }, [apocFlashDurationMs]);
  useEffect(() => { saveApocalypseFlashGapMs(apocFlashGapMs); }, [apocFlashGapMs]);
  // (cue-dot position persistence removed — no longer configurable)
  useEffect(() => { saveApocalypseCueDotSizePx(apocCueDotSizePx); }, [apocCueDotSizePx]);
  useEffect(() => { saveApocalypseCueDotColor(apocCueDotColor); }, [apocCueDotColor]);
  useEffect(() => { saveApocalypseCueDotOpacityPct(apocCueDotOpacityPct); }, [apocCueDotOpacityPct]);
  useEffect(() => { saveApocalypseCueDotLeadSec(apocCueDotLeadSec); }, [apocCueDotLeadSec]);
  // Pass-4 persistence.
  useEffect(() => { saveApocalypseWipeMode(apocWipeMode); }, [apocWipeMode]);
  useEffect(() => { saveApocalypseErrorEnabled(apocErrorEnabled); }, [apocErrorEnabled]);
  useEffect(() => { saveApocalypseErrorTitle(apocErrorTitle); }, [apocErrorTitle]);
  useEffect(() => { saveApocalypseErrorBody(apocErrorBody); }, [apocErrorBody]);
  useEffect(() => { saveApocalypseErrorSoundVolume(apocErrorSoundVolume); }, [apocErrorSoundVolume]);
  useEffect(() => { saveApocalypseVibrationEnabled(apocVibrationEnabled); }, [apocVibrationEnabled]);
  useEffect(() => { saveApocalypseVibrationVolume(apocVibrationVolume); }, [apocVibrationVolume]);
  useEffect(() => { saveApocalypseSecondTemplate(apocSecondTemplate); }, [apocSecondTemplate]);
  useEffect(() => { saveApocalypseShakeMode(apocShakeMode); }, [apocShakeMode]);
  useEffect(() => { saveApocalypsePushEnabled(apocPushEnabled); }, [apocPushEnabled]);
  useEffect(() => { saveApocalypsePushSoundEnabled(apocPushSoundEnabled); }, [apocPushSoundEnabled]);
  useEffect(() => { saveApocalypseScrollSpeedMs(apocScrollSpeedMs); }, [apocScrollSpeedMs]);
  useEffect(() => { saveApocalypseRestoreFlashEnabled(apocRestoreFlashEnabled); }, [apocRestoreFlashEnabled]);
  useEffect(() => { saveApocalypseRestoreFlashMs(apocRestoreFlashMs); }, [apocRestoreFlashMs]);
  useEffect(() => { localStorage.setItem(STORAGE.APOCALYPSE_TEMPLATE, apocTemplate); }, [apocTemplate]);
  useEffect(() => {
    // Wallpaper is a data: URL; clearing it removes the key entirely so
    // the lockscreen falls back to the default gradient.
    if (apocWallpaper) localStorage.setItem(STORAGE.APOCALYPSE_WALLPAPER, apocWallpaper);
    else localStorage.removeItem(STORAGE.APOCALYPSE_WALLPAPER);
  }, [apocWallpaper]);
  useEffect(() => { saveApocalypseWipeFrameMs(apocWipeFrameMs); }, [apocWipeFrameMs]);
  useEffect(() => { saveApocalypsePostWipeDelayMs(apocPostWipeDelayMs); }, [apocPostWipeDelayMs]);
  useEffect(() => { saveApocalypseShakeThreshold(apocShakeThreshold); }, [apocShakeThreshold]);
  useEffect(() => { saveApocalypseShakeCount(apocShakeCount); }, [apocShakeCount]);
  useEffect(() => { saveApocalypseAutoMotion(apocAutoMotion); }, [apocAutoMotion]);
  useEffect(() => { saveApocalypseFxPermEnabled(apocFxPermEnabled); }, [apocFxPermEnabled]);
  useEffect(() => { saveApocalypseFxPermType(apocFxPermType); }, [apocFxPermType]);
  useEffect(() => { saveApocalypseFxSpeechEnabled(apocFxSpeechEnabled); }, [apocFxSpeechEnabled]);
  useEffect(() => { saveApocalypseFxSpeechTemplate(apocFxSpeechTemplate); }, [apocFxSpeechTemplate]);
  useEffect(() => { saveApocalypseFxSpeechVoice(apocFxSpeechVoice); }, [apocFxSpeechVoice]);
  useEffect(() => { saveApocalypseFxSpeechRate(apocFxSpeechRate); }, [apocFxSpeechRate]);
  useEffect(() => { saveApocalypseFxSpeechPitch(apocFxSpeechPitch); }, [apocFxSpeechPitch]);
  useEffect(() => { saveApocalypseFxClipboardEnabled(apocFxClipboardEnabled); }, [apocFxClipboardEnabled]);
  useEffect(() => { saveApocalypseFxClipboardTemplate(apocFxClipboardTemplate); }, [apocFxClipboardTemplate]);
  useEffect(() => { saveApocalypseFxThemeEnabled(apocFxThemeEnabled); }, [apocFxThemeEnabled]);
  useEffect(() => {
    // Parse the comma-separated hex list, save the cleaned form back.
    const arr = (apocFxThemeColors || "")
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    saveApocalypseFxThemeColors(arr);
  }, [apocFxThemeColors]);
  useEffect(() => { saveApocalypseFxThemeInterval(apocFxThemeInterval); }, [apocFxThemeInterval]);
  useEffect(() => { saveApocalypseExtrasCount(apocExtrasCount); }, [apocExtrasCount]);
  useEffect(() => { saveApocalypseExtrasInterval(apocExtrasInterval); }, [apocExtrasInterval]);
  useEffect(() => { saveApocalypseExtrasTitles(apocExtrasTitles); }, [apocExtrasTitles]);
  useEffect(() => { saveApocalypseFlickerMs(apocFlickerMs); }, [apocFlickerMs]);
  useEffect(() => { saveApocalypseFlashColor(0, apocFlash1); }, [apocFlash1]);
  useEffect(() => { saveApocalypseFlashColor(1, apocFlash2); }, [apocFlash2]);
  useEffect(() => { saveApocalypseFlashColor(2, apocFlash3); }, [apocFlash3]);
  useEffect(() => { saveApocalypseFlashColor(3, apocFlash4); }, [apocFlash4]);
  useEffect(() => { saveApocalypseRowStagger(apocRowStagger); }, [apocRowStagger]);
  useEffect(() => { saveApocalypseRowCycle(apocRowCycle); }, [apocRowCycle]);
  useEffect(() => { saveApocalypseRowFrame(apocRowFrame); }, [apocRowFrame]);

  // Mutate-by-index helpers for the combo list. Combo dirs are 1–3 chars
  // from "URDL"; we round-trip through three slot dropdowns (_/U/R/D/L)
  // for the UX, collapsing back to the storage form on save.
  const setComboSlot = useCallback((id, slotIdx, value) => {
    setSwiperCombos((list) =>
      list.map((c) => {
        if (c.id !== id) return c;
        const slots = padSlots(c.dirs);
        slots[slotIdx] = value;
        // First slot empty → drop the row entirely on next save by
        // forcing a minimum: we keep it editable but the saved value
        // is normalised in saveSwiperCombos.
        return { ...c, dirs: slots.join("").replace(/_+/g, "") };
      })
    );
  }, []);

  const setComboWord = useCallback((id, word) => {
    setSwiperCombos((list) =>
      list.map((c) => (c.id === id ? { ...c, word } : c))
    );
  }, []);

  const addCombo = useCallback(() => {
    setSwiperCombos((list) => [...list, { id: makeSwiperComboId(), dirs: "U", word: "" }]);
  }, []);

  const removeCombo = useCallback((id) => {
    setSwiperCombos((list) => list.filter((c) => c.id !== id));
  }, []);

  // Detect duplicate dir sequences (visually flag — last wins at runtime).
  const dirCounts = useMemo(() => {
    const counts = {};
    swiperCombos.forEach((c) => {
      const key = c.dirs;
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [swiperCombos]);

  const updateDecoy = (i, field, value) =>
    setDecoys((d) => d.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  const removeDecoy = (i) => setDecoys((d) => d.filter((_, idx) => idx !== i));
  const addDecoy = () => setDecoys((d) => [...d, newDecoy()]);
  const resetDecoys = () => setDecoys(DEFAULT_DECOYS);
  const DECOY_PAGE_SIZE = 20;
  // `visibleDecoyCount` is the magician's *desired* window size; the
  // actually-rendered count clamps it against the current list length
  // during render, so we never need to sync with a useEffect.
  const [visibleDecoyCount, setVisibleDecoyCount] = useState(DECOY_PAGE_SIZE);
  const renderedDecoyCount = Math.min(visibleDecoyCount, decoys.length);
  const [shuffleCount, setShuffleCount] = useState(() => {
    const raw = parseInt(localStorage.getItem(STORAGE.DECOY_SHUFFLE_COUNT) || "", 10);
    return Number.isFinite(raw) && raw >= 1 && raw <= MAX_DECOYS ? raw : 6;
  });
  useEffect(() => {
    localStorage.setItem(STORAGE.DECOY_SHUFFLE_COUNT, String(shuffleCount));
  }, [shuffleCount]);
  const shuffleDecoysAction = useCallback(() => {
    const langCode = language === "auto"
      ? (navigator.language || "en")
      : language;
    setDecoys(shuffleDecoys(langCode, shuffleCount));
  }, [language, shuffleCount]);

  // --- Wallpaper upload --------------------------------------------------
  // Resize whatever the magician picks down to a sensible mobile size
  // (max 1080px on the long edge) and re-encode as JPEG so we don't blow
  // through the ~5 MB localStorage budget. The shrunk data URL is what
  // gets persisted and rendered on /lock.
  const onWallpaperPick = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = String(e.target?.result || "");
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        const MAX = 1080;
        let w = img.width;
        let h = img.height;
        if (w > h && w > MAX) { h = Math.round((h * MAX) / w); w = MAX; }
        else if (h >= w && h > MAX) { w = Math.round((w * MAX) / h); h = MAX; }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setApocWallpaper(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          const compressed = canvas.toDataURL("image/jpeg", 0.82);
          setApocWallpaper(compressed);
        } catch {
          // Tainted canvas / huge image — fall back to the raw data URL.
          setApocWallpaper(src);
        }
      };
      img.onerror = () => setApocWallpaper(src);
      img.src = src;
    };
    reader.readAsDataURL(file);
  }, []);

  return (
    <div
      className="min-h-screen w-full px-5 py-8 sm:py-12 mr-luxe mr-grain"
      style={{
        background:
          "radial-gradient(900px 500px at 85% 5%, rgba(220,180,60,0.10), transparent 60%), radial-gradient(1100px 700px at 15% 105%, rgba(180,28,40,0.48), transparent 55%), radial-gradient(800px 500px at 50% 50%, rgba(140,20,30,0.18), transparent 70%), #0a0306",
      }}
      data-testid="settings-page"
    >
      <div className="mr-dust" aria-hidden="true" />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-10 mr-rise">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-white/70 hover:text-white hover:bg-white/5 -ml-2"
            data-testid="back-to-setup"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="w-16" />
          <div className="w-16" />
        </div>

        <header className="mb-10 flex flex-col items-center text-center mr-rise delay-1">
          <BrandWordmark size="lg" tagline="Configuration" aura />
          <div className="mt-5 mr-rule" style={{ width: "3rem" }} />
        </header>

        {/* About — the story moved here from the home screen.
            Kept brief; this is the first card a magician sees in Settings. */}
        <Card
          className="border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mr-rise delay-1 mb-5"
          data-testid="about-card"
        >
          <p
            className="text-[14px] sm:text-[15px] text-white/75 leading-relaxed"
            data-testid="setup-story"
          >
            One app that does everything &mdash; the secret weapon a
            mentalist can carry, hidden in plain sight, inside a Notes icon
            on the home screen.
          </p>
          <p className="mt-3 text-[13px] sm:text-[14px] text-white/55 leading-relaxed">
            Pick a row, run an effect. The fake folder list hides four
            different tricks behind one ordinary Notes icon &mdash; ready
            the moment you are.
          </p>

          {/* Tutorial / Admin shortcuts — secondary navigation lives here
              now so the home screen stays uncluttered. */}
          <div className="mt-5 pt-5 border-t border-white/[0.06] flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/tutorial")}
              className="h-9 px-3 text-white/75 hover:text-white hover:bg-white/5 rounded-xl"
              data-testid="open-tutorial-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" /> The Manual
            </Button>
            {localStorage.getItem(STORAGE.IS_ADMIN) === "1" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="h-9 px-3 text-white/75 hover:text-white hover:bg-white/5 rounded-xl"
                data-testid="open-admin-btn"
              >
                <Users className="w-4 h-4 mr-2" /> The Circle
              </Button>
            )}
          </div>
        </Card>

        {/* Routing overview — single source of truth so the magician knows
            which gesture goes where. */}
        <Card
          className="border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mr-rise delay-2"
          data-testid="routing-card"
        >
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Sparkles className="w-4 h-4" style={{ color: "#e85a64" }} /> Effect routing
          </div>
          <p className="text-xs text-white/55 mt-1 leading-relaxed">
            Each entry point on the fake Notes folder runs its own effect. No more global picker.
          </p>
          <ul className="mt-4 space-y-2 text-[13px] text-white/75 leading-snug">
            <li><span className="text-[#e85a64] font-semibold">Top note</span> &nbsp;→&nbsp; Swiper prediction (cards or custom words)</li>
            <li><span className="text-[#e85a64] font-semibold">Third note</span> &nbsp;→&nbsp; Ten Outs (cycling prediction, locks on phone flip)</li>
            <li><span className="text-[#e85a64] font-semibold">+ button</span> &nbsp;→&nbsp; Multi-question (up to 3 prompts)</li>
            <li><span className="text-white/45">Other rows</span> &nbsp;→&nbsp; Plain compose note (no AI)</li>
            <li className="pt-1 text-white/55"><span className="text-[#e85a64] font-semibold">Pencil icon (any note)</span> &nbsp;→&nbsp; secretly arms Add-a-Number on the note you&rsquo;re in</li>
            <li className="text-white/55"><span className="text-[#e85a64] font-semibold">Long-press &ldquo;X notes&rdquo;</span> &nbsp;→&nbsp; fake lockscreen for Notes Apocalypse</li>
          </ul>
        </Card>

        {/* Note defaults — opening title */}
        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mr-rise delay-2"
          data-testid="defaults-card"
        >
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <NotebookPen className="w-4 h-4" style={{ color: "#e85a64" }} /> Note defaults
          </div>
          <p className="text-xs text-white/55 mt-1">
            Each entry point can open with its own title.
          </p>

          <div className="mt-4">
            <Label htmlFor="default-title-input" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Default title (decoy rows)
            </Label>
            <Input
              id="default-title-input"
              type="text"
              value={defaultTitle}
              onChange={(e) => setDefaultTitle(e.target.value)}
              placeholder='e.g. "Grocery list"'
              maxLength={80}
              className="mt-2 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="default-title-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Fallback for note rows that don&apos;t have their own title set in Decoys below.
            </p>
          </div>

          <div className="mt-5">
            <Label htmlFor="compose-title-input" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Compose title (+ button)
            </Label>
            <Input
              id="compose-title-input"
              type="text"
              value={composeTitle}
              onChange={(e) => setComposeTitle(e.target.value)}
              placeholder='e.g. "Quick math"'
              maxLength={80}
              className="mt-2 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="compose-title-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Used when you tap the + button on /notes. Leave blank to use the default.
            </p>
          </div>
        </Card>

        {/* Reveal style */}
        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mr-rise delay-3"
          data-testid="reveal-card"
        >
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Wand2 className="w-4 h-4" style={{ color: "#e85a64" }} /> Reveal
          </div>
          <p className="text-xs text-white/55 mt-1">
            Choose how the answer comes back to you. Applies to + compose notes only.
          </p>

          <div className="mt-4 grid grid-cols-3 gap-2 p-1 bg-black/30 rounded-xl border border-white/5">
            {REVEAL_STYLES.map((r) => {
              const active = revealStyle === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRevealStyle(r.id)}
                  className={`px-2 py-3 rounded-lg text-left transition-all ${
                    active ? "text-[#f0ead4] shadow" : "bg-transparent text-white/70 hover:bg-white/5"
                  }`}
                  style={active ? {
                    background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
                    boxShadow: "inset 0 0 0 1px rgba(214,201,140,0.25), 0 6px 18px -8px rgba(200,32,44,0.6)",
                  } : undefined}
                  data-testid={`reveal-${r.id}`}
                >
                  <div className="text-sm font-semibold">{r.label}</div>
                  <div className={`text-[11px] mt-0.5 leading-tight ${active ? "text-[#f0ead4]/75" : "text-white/45"}`}>
                    {r.desc}
                  </div>
                </button>
              );
            })}
          </div>

          {revealStyle === "scroll" && (
            <div className="mt-5 pt-5 border-t border-white/[0.06]" data-testid="prefix-row">
              <Label htmlFor="prefix-input" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                Prediction prefix
              </Label>
              <Input
                id="prefix-input"
                type="text"
                value={predictionPrefix}
                onChange={(e) => setPredictionPrefix(e.target.value)}
                placeholder='e.g. "I predicted you would say:"'
                className="mt-2 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
                data-testid="prefix-input"
              />
              <p className="text-xs text-white/45 mt-2 leading-relaxed">
                Optional text that gets typed in front of the answer below the fold.
                Leave blank for just the bare answer.
              </p>
            </div>
          )}
        </Card>

        {/* Backdate timestamp */}
        <BackdateCard backdateMin={backdateMin} setBackdateMin={setBackdateMin} />

        {/* Compact reveal — abbreviate the subliminal date/zodiac/weekday */}
        <CompactRevealCard
          compactReveal={compactReveal}
          setCompactReveal={setCompactReveal}
        />

        {/* Realism: haptic feedback for tap/unlock/success/error */}
        <HapticsCard
          hapticsEnabled={hapticsEnabled}
          setHapticsEnabled={setHapticsEnabled}
        />

        {/* Realism: restore decoy note contents on Back */}
        <DecoyResetCard
          decoyResetOnExit={decoyResetOnExit}
          setDecoyResetOnExit={setDecoyResetOnExit}
        />

        {/* Realism: iCloud "storage almost full" banner on /notes */}
        <ICloudBannerCard
          iCloudBannerEnabled={iCloudBannerEnabled}
          setICloudBannerEnabled={setICloudBannerEnabled}
        />

        {/* Phantom Peek — silently capture a (+) note the spectator
            wrote and then swipe-deleted, surface it on h1 tap. */}
        <PhantomPeekCard
          peekEnabled={peekEnabled}
          setPeekEnabled={setPeekEnabled}
          peekDurationMs={peekDurationMs}
          setPeekDurationMs={setPeekDurationMs}
          peekPassMotion={peekPassMotion}
          setPeekPassMotion={setPeekPassMotion}
          peekPassThreshold={peekPassThreshold}
          setPeekPassThreshold={setPeekPassThreshold}
        />

        {/* Swiper Mode — top decoy prediction */}
        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
          data-testid="swiper-card"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
                <Layers className="w-4 h-4" style={{ color: "#e85a64" }} /> Swiper (top note)
              </div>
              <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
                Encode a card with directional flicks; the top decoy opens
                pre-written. <span className="text-white/75">The Manual</span> covers the full gesture set.
              </p>
            </div>
            <Switch checked={swiperEnabled} onCheckedChange={setSwiperEnabled} data-testid="swiper-switch" />
          </div>

          {swiperEnabled && (
            <div className="mt-5 space-y-5">
              {/* Mode picker: Playing cards vs Custom words */}
              <div>
                <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Swipe mode
                </Label>
                <div className="mt-3 grid grid-cols-2 gap-2 p-1 bg-black/30 rounded-xl border border-white/5">
                  {[
                    { id: "cards",  label: "Playing cards", desc: "3-swipe encoding (52 cards + Joker)" },
                    { id: "custom", label: "Custom words",  desc: "1 swipe per direction (4 outcomes)" },
                  ].map((m) => {
                    const active = swiperMode === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSwiperMode(m.id)}
                        className={`px-2 py-3 rounded-lg text-left transition-all ${
                          active ? "text-[#f0ead4] shadow" : "bg-transparent text-white/70 hover:bg-white/5"
                        }`}
                        style={active ? {
                          background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
                          boxShadow: "inset 0 0 0 1px rgba(214,201,140,0.25), 0 6px 18px -8px rgba(200,32,44,0.6)",
                        } : undefined}
                        data-testid={`swiper-mode-${m.id}`}
                      >
                        <div className="text-sm font-semibold">{m.label}</div>
                        <div className={`text-[11px] mt-0.5 leading-tight ${active ? "text-[#f0ead4]/75" : "text-white/45"}`}>
                          {m.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {swiperMode === "cards" && (
                <>
                  {/* Cards cheat-sheet */}
                  <div
                    className="rounded-xl p-4 border border-white/[0.07]"
                    style={{ background: "rgba(0,0,0,0.32)" }}
                    data-testid="swiper-cheatsheet"
                  >
                    <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-3 block">
                      Clock-swipe cheat-sheet
                    </Label>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] font-mono" style={{ color: "rgba(214,201,140,0.85)" }}>
                      <span>↑↑ &nbsp;Queen</span>     <span>↓→ &nbsp;5</span>
                      <span>↑→ &nbsp;Ace</span>       <span>↓↓ &nbsp;6</span>
                      <span>↑← &nbsp;Jack</span>      <span>↓← &nbsp;7</span>
                      <span>↑↓ &nbsp;King</span>      <span>←↓ &nbsp;8</span>
                      <span>→↑ &nbsp;2</span>         <span>←← &nbsp;9</span>
                      <span>→→ &nbsp;3</span>         <span>←↑ &nbsp;10</span>
                      <span>→↓ &nbsp;4</span>         <span>→← &nbsp;Joker</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/5 text-[12px] leading-relaxed text-white/65">
                      <strong className="text-white/85">3rd swipe — suit:</strong>{" "}
                      <span className="text-white/85">↑ ♠</span> ·{" "}
                      <span className="text-white/85">→ ♥</span> ·{" "}
                      <span className="text-white/85">↓ ♣</span> ·{" "}
                      <span className="text-white/85">← ♦</span>
                      <br />
                      <span className="text-white/45">Joker locks in after 2 swipes — no suit needed.</span>
                    </div>
                  </div>

                  {/* Prediction text — always a custom sentence with (xxx) */}
                  <div className="pt-2 border-t border-white/[0.06]">
                    <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Prediction sentence
                    </Label>
                    <Input
                      type="text"
                      value={swiperTemplate}
                      onChange={(e) => setSwiperTemplate(e.target.value)}
                      placeholder='e.g. "I always knew it would be (xxx)!"'
                      className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
                      data-testid="swiper-template-input"
                    />
                    <p className="text-xs text-white/45 mt-2 leading-relaxed">
                      Write any sentence and put <span className="text-white/70">(xxx)</span> where
                      the card should appear. Leave blank to drop the bare card name straight in.
                    </p>
                  </div>
                </>
              )}

              {swiperMode === "custom" && (
                <>
                  {/* Combo editor — any sequence of 1-3 swipes → word */}
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Swipe combos
                    </Label>
                    <p className="text-xs text-white/45 mt-1 leading-relaxed">
                      Build your own gestures. Pick 1, 2 or 3 arrows for each
                      row and type the word that should fill <span className="text-white/70">(xxx)</span>{" "}
                      when you perform that sequence on the folder list.
                    </p>

                    <div className="mt-3 space-y-2">
                      {swiperCombos.map((c) => {
                        const slots = padSlots(c.dirs);
                        const dup = c.dirs && dirCounts[c.dirs] > 1;
                        return (
                          <div
                            key={c.id}
                            className="rounded-lg border border-white/[0.07] p-2"
                            style={{ background: "rgba(0,0,0,0.32)" }}
                            data-testid={`swiper-combo-${c.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {/* 3 direction dropdowns */}
                              <div className="flex items-center gap-1 shrink-0">
                                {[0, 1, 2].map((idx) => (
                                  <DirPicker
                                    key={idx}
                                    value={slots[idx]}
                                    onChange={(v) => setComboSlot(c.id, idx, v)}
                                    testid={`combo-${c.id}-slot-${idx}`}
                                  />
                                ))}
                              </div>
                              <Input
                                type="text"
                                value={c.word}
                                onChange={(e) => setComboWord(c.id, e.target.value)}
                                placeholder="word to substitute"
                                className="h-9 bg-transparent border-white/10 text-white placeholder:text-white/30 text-sm"
                                data-testid={`combo-${c.id}-word`}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeCombo(c.id)}
                                className="text-white/40 hover:text-red-300 hover:bg-white/5 shrink-0 h-8 w-8"
                                aria-label="remove combo"
                                data-testid={`combo-${c.id}-remove`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            {dup && (
                              <p className="text-[11px] text-amber-300/80 mt-1.5 ml-1">
                                Duplicate sequence — the last row wins at performance time.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      variant="ghost"
                      onClick={addCombo}
                      className="mt-3 w-full hover:bg-white/5 border border-dashed rounded-lg h-9 text-sm"
                      style={{ color: "#e85a64", borderColor: "rgba(200,32,44,0.45)" }}
                      data-testid="swiper-combo-add"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Add combo
                    </Button>

                    <p className="text-xs text-white/40 mt-3 leading-relaxed">
                      Tips: single-arrow rows fire instantly. Multi-arrow rows
                      have a short pause-to-finalize window (about 0.7&nbsp;s
                      between flicks). Sequences with no defined match are
                      silently dropped.
                    </p>
                  </div>

                  {/* Custom-mode template */}
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Prediction sentence
                    </Label>
                    <Input
                      type="text"
                      value={swiperTemplate}
                      onChange={(e) => setSwiperTemplate(e.target.value)}
                      placeholder='e.g. "I had a feeling you&rsquo;d say (xxx)."'
                      className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
                      data-testid="swiper-custom-template-input"
                    />
                    <p className="text-xs text-white/45 mt-2 leading-relaxed">
                      Optional. Leave blank to drop the bare word straight into the note.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Trident — second decoy note (chronological-order reveal) */}
        <TridentCard
          tridentEnabled={tridentEnabled}
          setTridentEnabled={setTridentEnabled}
          tridentTemplate={tridentTemplate}
          setTridentTemplate={setTridentTemplate}
          DEFAULT_TRIDENT_TEMPLATE={DEFAULT_TRIDENT_TEMPLATE}
        />

        {/* Search-Force Prediction — fake old note atop search results */}
        <SearchForceCard
          searchForceEnabled={searchForceEnabled}
          setSearchForceEnabled={setSearchForceEnabled}
          searchForceTitle={searchForceTitle}
          setSearchForceTitle={setSearchForceTitle}
          searchForceTemplate={searchForceTemplate}
          setSearchForceTemplate={setSearchForceTemplate}
          searchForceDateLabel={searchForceDateLabel}
          setSearchForceDateLabel={setSearchForceDateLabel}
          searchForceDateISO={searchForceDateISO}
          setSearchForceDateISO={setSearchForceDateISO}
          searchForceDateOffset={searchForceDateOffset}
          setSearchForceDateOffset={setSearchForceDateOffset}
          searchForceQ1Cancel={searchForceQ1Cancel}
          setSearchForceQ1Cancel={setSearchForceQ1Cancel}
          searchForceQ1Emergency={searchForceQ1Emergency}
          setSearchForceQ1Emergency={setSearchForceQ1Emergency}
          searchForceQ2Cancel={searchForceQ2Cancel}
          setSearchForceQ2Cancel={setSearchForceQ2Cancel}
          searchForceQ2Emergency={searchForceQ2Emergency}
          setSearchForceQ2Emergency={setSearchForceQ2Emergency}
          searchForceQ3Cancel={searchForceQ3Cancel}
          setSearchForceQ3Cancel={setSearchForceQ3Cancel}
          searchForceQ3Emergency={searchForceQ3Emergency}
          setSearchForceQ3Emergency={setSearchForceQ3Emergency}
          searchForceQ4Cancel={searchForceQ4Cancel}
          setSearchForceQ4Cancel={setSearchForceQ4Cancel}
          searchForceQ4Emergency={searchForceQ4Emergency}
          setSearchForceQ4Emergency={setSearchForceQ4Emergency}
          searchForceArmedInfo={searchForceArmedInfo}
          onClearSearchForceArmed={handleClearSearchForceArmed}
          DEFAULT_SEARCH_FORCE_TITLE={DEFAULT_SEARCH_FORCE_TITLE}
          DEFAULT_SEARCH_FORCE_TEMPLATE={DEFAULT_SEARCH_FORCE_TEMPLATE}
          DEFAULT_SEARCH_FORCE_DATE_LABEL={DEFAULT_SEARCH_FORCE_DATE_LABEL}
          DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES={DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES}
          DEFAULT_SEARCH_FORCE_Q1_CANCEL={DEFAULT_SEARCH_FORCE_Q1_CANCEL}
          DEFAULT_SEARCH_FORCE_Q1_EMERGENCY={DEFAULT_SEARCH_FORCE_Q1_EMERGENCY}
          DEFAULT_SEARCH_FORCE_Q2_CANCEL={DEFAULT_SEARCH_FORCE_Q2_CANCEL}
          DEFAULT_SEARCH_FORCE_Q2_EMERGENCY={DEFAULT_SEARCH_FORCE_Q2_EMERGENCY}
          DEFAULT_SEARCH_FORCE_Q3_CANCEL={DEFAULT_SEARCH_FORCE_Q3_CANCEL}
          DEFAULT_SEARCH_FORCE_Q3_EMERGENCY={DEFAULT_SEARCH_FORCE_Q3_EMERGENCY}
          DEFAULT_SEARCH_FORCE_Q4_CANCEL={DEFAULT_SEARCH_FORCE_Q4_CANCEL}
          DEFAULT_SEARCH_FORCE_Q4_EMERGENCY={DEFAULT_SEARCH_FORCE_Q4_EMERGENCY}
        />

        {/* Flip Force — face-down dwell then flip-up force-opens a decoy */}
        <FlipForceCard
          flipForceEnabled={flipForceEnabled}
          setFlipForceEnabled={setFlipForceEnabled}
          flipForceTargetId={flipForceTargetId}
          setFlipForceTargetId={setFlipForceTargetId}
          flipForceTargetId2={flipForceTargetId2}
          setFlipForceTargetId2={setFlipForceTargetId2}
          flipForceTargetId3={flipForceTargetId3}
          setFlipForceTargetId3={setFlipForceTargetId3}
          flipForceMinFacedownMs={flipForceMinFacedownMs}
          setFlipForceMinFacedownMs={setFlipForceMinFacedownMs}
          decoys={decoys}
          setDecoys={setDecoys}
        />

        {/* Ten Outs — third decoy note */}
        <TenOutsCard
          tenOutsEnabled={tenOutsEnabled}
          setTenOutsEnabled={setTenOutsEnabled}
          tenOutsInterval={tenOutsInterval}
          setTenOutsIntervalState={setTenOutsIntervalState}
          tenOutsTemplate={tenOutsTemplate}
          setTenOutsTemplate={setTenOutsTemplate}
          tenOutsWords={tenOutsWords}
          setTenOutsWordsState={setTenOutsWordsState}
          MIN_INTERVAL_SEC={MIN_INTERVAL_SEC}
          MAX_INTERVAL_SEC={MAX_INTERVAL_SEC}
        />

        {/* Add-a-Number — armed per-note by tapping the pencil icon */}
        <AddANumberCard
          addNumForceTotal={addNumForceTotal}
          setAddNumForceTotal={setAddNumForceTotal}
          addNumSwapPosition={addNumSwapPosition}
          setAddNumSwapPosition={setAddNumSwapPosition}
          addNumTrigger={addNumTrigger}
          setAddNumTrigger={setAddNumTrigger}
          DEFAULT_FORCE_TOTAL={DEFAULT_FORCE_TOTAL}
        />

        {/* Notes Apocalypse — extracted into ApocalypseSettings.jsx */}
        <ApocalypseSettings
          apocEnabled={apocEnabled} setApocEnabled={setApocEnabled}
          apocLength={apocLength} setApocLength={setApocLength}
          apocDelay={apocDelay} setApocDelay={setApocDelay}
          apocTemplate={apocTemplate} setApocTemplate={setApocTemplate}
          apocWallpaper={apocWallpaper} setApocWallpaper={setApocWallpaper}
          apocResetPreset={apocResetPreset} setApocResetPreset={setApocResetPreset}
          apocFlash1={apocFlash1} setApocFlash1={setApocFlash1}
          apocFlash2={apocFlash2} setApocFlash2={setApocFlash2}
          apocFlash3={apocFlash3} setApocFlash3={setApocFlash3}
          apocFlash4={apocFlash4} setApocFlash4={setApocFlash4}
          apocFlashCount={apocFlashCount} setApocFlashCount={setApocFlashCount}
          apocFlashDelayMs={apocFlashDelayMs} setApocFlashDelayMs={setApocFlashDelayMs}
          apocFlashDurationMs={apocFlashDurationMs} setApocFlashDurationMs={setApocFlashDurationMs}
          apocFlashGapMs={apocFlashGapMs} setApocFlashGapMs={setApocFlashGapMs}
          apocAutoMotion={apocAutoMotion} setApocAutoMotion={setApocAutoMotion}
          apocShakeThreshold={apocShakeThreshold} setApocShakeThreshold={setApocShakeThreshold}
          apocShakeCount={apocShakeCount} setApocShakeCount={setApocShakeCount}
          apocMotionStatus={apocMotionStatus} setApocMotionStatus={setApocMotionStatus}
          apocWipeFrameMs={apocWipeFrameMs} setApocWipeFrameMs={setApocWipeFrameMs}
          apocPostWipeDelayMs={apocPostWipeDelayMs} setApocPostWipeDelayMs={setApocPostWipeDelayMs}
          apocExtrasCount={apocExtrasCount} setApocExtrasCount={setApocExtrasCount}
          apocExtrasInterval={apocExtrasInterval} setApocExtrasInterval={setApocExtrasInterval}
          apocExtrasTitles={apocExtrasTitles} setApocExtrasTitles={setApocExtrasTitles}
          apocFlickerMs={apocFlickerMs} setApocFlickerMs={setApocFlickerMs}
          apocRowStagger={apocRowStagger} setApocRowStagger={setApocRowStagger}
          apocRowCycle={apocRowCycle} setApocRowCycle={setApocRowCycle}
          apocRowFrame={apocRowFrame} setApocRowFrame={setApocRowFrame}
          apocWipeMode={apocWipeMode} setApocWipeMode={setApocWipeMode}
          apocErrorEnabled={apocErrorEnabled} setApocErrorEnabled={setApocErrorEnabled}
          apocErrorTitle={apocErrorTitle} setApocErrorTitle={setApocErrorTitle}
          apocErrorBody={apocErrorBody} setApocErrorBody={setApocErrorBody}
          apocErrorSoundVolume={apocErrorSoundVolume} setApocErrorSoundVolume={setApocErrorSoundVolume}
          apocVibrationEnabled={apocVibrationEnabled} setApocVibrationEnabled={setApocVibrationEnabled}
          apocVibrationVolume={apocVibrationVolume} setApocVibrationVolume={setApocVibrationVolume}
          apocSecondTemplate={apocSecondTemplate} setApocSecondTemplate={setApocSecondTemplate}
          apocShakeMode={apocShakeMode} setApocShakeMode={setApocShakeMode}
          apocPushEnabled={apocPushEnabled} setApocPushEnabled={setApocPushEnabled}
          apocPushSoundEnabled={apocPushSoundEnabled} setApocPushSoundEnabled={setApocPushSoundEnabled}
          apocScrollSpeedMs={apocScrollSpeedMs} setApocScrollSpeedMs={setApocScrollSpeedMs}
          apocRestoreFlashEnabled={apocRestoreFlashEnabled} setApocRestoreFlashEnabled={setApocRestoreFlashEnabled}
          apocRestoreFlashMs={apocRestoreFlashMs} setApocRestoreFlashMs={setApocRestoreFlashMs}
          apocFxFeedback={apocFxFeedback} setApocFxFeedback={setApocFxFeedback}
          apocFxPermEnabled={apocFxPermEnabled} setApocFxPermEnabled={setApocFxPermEnabled}
          apocFxPermType={apocFxPermType} setApocFxPermType={setApocFxPermType}
          apocFxSpeechEnabled={apocFxSpeechEnabled} setApocFxSpeechEnabled={setApocFxSpeechEnabled}
          apocFxSpeechTemplate={apocFxSpeechTemplate} setApocFxSpeechTemplate={setApocFxSpeechTemplate}
          apocFxSpeechVoice={apocFxSpeechVoice} setApocFxSpeechVoice={setApocFxSpeechVoice}
          apocFxSpeechRate={apocFxSpeechRate} setApocFxSpeechRate={setApocFxSpeechRate}
          apocFxSpeechPitch={apocFxSpeechPitch} setApocFxSpeechPitch={setApocFxSpeechPitch}
          apocFxClipboardEnabled={apocFxClipboardEnabled} setApocFxClipboardEnabled={setApocFxClipboardEnabled}
          apocFxClipboardTemplate={apocFxClipboardTemplate} setApocFxClipboardTemplate={setApocFxClipboardTemplate}
          apocFxThemeEnabled={apocFxThemeEnabled} setApocFxThemeEnabled={setApocFxThemeEnabled}
          apocFxThemeInterval={apocFxThemeInterval} setApocFxThemeInterval={setApocFxThemeInterval}
          apocFxThemeColors={apocFxThemeColors} setApocFxThemeColors={setApocFxThemeColors}
          apocCueDotSizePx={apocCueDotSizePx} setApocCueDotSizePx={setApocCueDotSizePx}
          apocCueDotColor={apocCueDotColor} setApocCueDotColor={setApocCueDotColor}
          apocCueDotOpacityPct={apocCueDotOpacityPct} setApocCueDotOpacityPct={setApocCueDotOpacityPct}
          apocCueDotLeadSec={apocCueDotLeadSec} setApocCueDotLeadSec={setApocCueDotLeadSec}
          apocCinematicOpen={apocCinematicOpen} setApocCinematicOpen={setApocCinematicOpen}
          rehearseValue={rehearseValue} setRehearseValue={setRehearseValue}
          rehearseSuit={rehearseSuit} setRehearseSuit={setRehearseSuit}
          onWallpaperPick={onWallpaperPick}
          voicesList={voicesList}
          navigate={navigate}
        />

        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
          data-testid="language-card"
        >
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Languages className="w-4 h-4" style={{ color: "#e85a64" }} /> Language
          </div>

          <div className="mt-4">
            <Label htmlFor="lang" className="sr-only">Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger id="lang" className="bg-black/30 border-white/10 text-white h-11" data-testid="language-select">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 border-white/10 text-white max-h-[60vh]">
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code} className="text-white focus:bg-white/10 focus:text-white">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Decoy notes editor */}
        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
          data-testid="decoy-card"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
              <FolderClosed className="w-4 h-4" style={{ color: "#e85a64" }} /> Decoy notes
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={1}
                max={MAX_DECOYS}
                value={shuffleCount}
                onChange={(e) => {
                  const raw = parseInt(e.target.value, 10);
                  if (Number.isFinite(raw)) {
                    setShuffleCount(Math.max(1, Math.min(MAX_DECOYS, raw)));
                  } else if (e.target.value === "") {
                    setShuffleCount(1);
                  }
                }}
                className="w-14 h-8 px-2 rounded-md bg-white/5 border border-white/10 text-white text-xs text-center focus:outline-none focus:border-white/30"
                data-testid="decoy-shuffle-count"
                aria-label={`How many notes to generate (1–${MAX_DECOYS})`}
                title={`How many notes to generate (1–${MAX_DECOYS})`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={shuffleDecoysAction}
                className="text-white/60 hover:text-white hover:bg-white/5"
                data-testid="decoy-shuffle"
                title="Replace with random, language-aware decoys"
              >
                <Shuffle className="w-3.5 h-3.5 mr-1" /> Shuffle
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetDecoys}
                className="text-white/60 hover:text-white hover:bg-white/5 -mr-2"
                data-testid="decoy-reset"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {decoys.slice(0, renderedDecoyCount).map((n, i) => {
              const tag = i === 0 ? "Swiper" : i === 2 ? "Ten Outs" : "Plain";
              return (
                <div
                  key={n.id}
                  className="bg-black/30 border border-white/5 rounded-xl p-3"
                  data-testid={`decoy-edit-row-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="shrink-0 text-[10px] uppercase tracking-[0.32em] px-2 py-1 rounded"
                      style={{
                        background: i < 2 ? "rgba(232,90,100,0.12)" : "rgba(255,255,255,0.05)",
                        color: i < 2 ? "#e85a64" : "rgba(255,255,255,0.45)",
                      }}
                    >
                      {tag}
                    </span>
                    <Input
                      value={n.title}
                      onChange={(e) => updateDecoy(i, "title", e.target.value)}
                      placeholder="Title (e.g. Groceries)"
                      className="bg-transparent border-white/10 text-white placeholder:text-white/30 h-9"
                      data-testid={`decoy-title-${i}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDecoy(i)}
                      className="text-white/40 hover:text-red-300 hover:bg-white/5 shrink-0"
                      aria-label="remove decoy"
                      data-testid={`decoy-remove-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    value={n.preview}
                    onChange={(e) => updateDecoy(i, "preview", e.target.value)}
                    placeholder="Preview snippet (optional)"
                    className="mt-2 bg-transparent border-white/10 text-white/80 placeholder:text-white/25 h-9 text-sm"
                    data-testid={`decoy-preview-${i}`}
                  />
                  {/* Per-row date+time override. Empty = auto label
                      (Today / Yesterday / weekday / full date). */}
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="datetime-local"
                      value={n.time || ""}
                      onChange={(e) => updateDecoy(i, "time", e.target.value)}
                      className="flex-1 bg-transparent border border-white/10 rounded-md text-white/80 h-9 px-2 text-sm placeholder:text-white/25 focus:outline-none focus:border-white/30"
                      data-testid={`decoy-time-${i}`}
                    />
                    {n.time ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => updateDecoy(i, "time", "")}
                        className="text-white/40 hover:text-white/85 hover:bg-white/5 shrink-0 h-9 w-9"
                        aria-label="clear date"
                        title="Clear — revert to auto"
                        data-testid={`decoy-time-clear-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.32em] text-white/40 px-2 shrink-0">
                        Auto
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            variant="ghost"
            onClick={addDecoy}
            className="mt-3 w-full hover:bg-white/5 border border-dashed rounded-xl h-10"
            style={{ color: "#e85a64", borderColor: "rgba(200,32,44,0.45)" }}
            data-testid="decoy-add"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add a decoy note
          </Button>

          {/* Paged-load footer — only renders when the decoy list is
              larger than the page size. Shows "Showing X of Y" and
              gives the magician a single tap to reveal the next page
              of rows, plus a quick toggle to collapse back. Keeps the
              Settings page light when 250 decoys are loaded. */}
          {decoys.length > DECOY_PAGE_SIZE && (
            <div
              className="mt-3 flex items-center justify-between gap-2 text-xs text-white/55"
              data-testid="decoy-paging"
            >
              <span data-testid="decoy-paging-status">
                Showing {renderedDecoyCount} of {decoys.length}
              </span>
              <div className="flex items-center gap-1">
                {renderedDecoyCount < decoys.length && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setVisibleDecoyCount((v) =>
                        Math.min(decoys.length, v + DECOY_PAGE_SIZE)
                      )
                    }
                    className="text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="decoy-show-more"
                  >
                    Show {Math.min(DECOY_PAGE_SIZE, decoys.length - renderedDecoyCount)} more
                  </Button>
                )}
                {renderedDecoyCount < decoys.length && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleDecoyCount(decoys.length)}
                    className="text-white/55 hover:text-white hover:bg-white/5"
                    data-testid="decoy-show-all"
                  >
                    Show all
                  </Button>
                )}
                {renderedDecoyCount > DECOY_PAGE_SIZE && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleDecoyCount(DECOY_PAGE_SIZE)}
                    className="text-white/55 hover:text-white hover:bg-white/5"
                    data-testid="decoy-collapse"
                  >
                    Collapse
                  </Button>
                )}
              </div>
            </div>
          )}

          <Button
            variant="ghost"
            onClick={() => navigate("/notes")}
            className="mt-2 w-full text-white/75 hover:text-white hover:bg-white/5 rounded-xl h-10"
            data-testid="decoy-preview"
          >
            Preview folder list
          </Button>
        </Card>

        <footer className="mt-12 flex flex-col items-center text-center gap-2 mr-rise delay-4">
          <div className="mr-rule" style={{ width: "2.5rem" }} />
          <p
            className="text-[10px] tracking-[0.42em] uppercase"
            style={{ color: "rgba(214,201,140,0.55)" }}
          >
            Conceived by Dennis Reinsma
          </p>
        </footer>
      </div>
    </div>
  );
}

/**
 * Single direction-slot dropdown for the swipe-combo editor. Renders the
 * familiar arrow glyphs; the "_" placeholder represents "unused slot"
 * (collapses out of the dirs string on save).
 */
function DirPicker({ value, onChange, testid }) {
  return (
    <Select value={value || "_"} onValueChange={onChange}>
      <SelectTrigger
        className="w-12 h-9 px-0 justify-center bg-transparent border-white/10 text-white text-base hover:bg-white/5"
        data-testid={testid}
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent className="min-w-[3rem] bg-neutral-900 border-white/10 text-white">
        {DIR_OPTIONS.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            className="justify-center text-center text-white focus:bg-white/10 focus:text-white"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Expand a combo dirs string ("URD") into 3 slot values for the dropdowns. */
function padSlots(dirs) {
  const arr = (dirs || "").split("");
  while (arr.length < 3) arr.push("_");
  return arr.slice(0, 3);
}
